// Lib imports
import _ from 'lodash/fp';

// Core imports
import GraniteError from 'granite-admin/utils/granite-error';
import { setAuthToken, resetAuthToken, resetSsoLogin } from 'granite-admin/utils/auth-singleton';
import { getOrganisation, getAuthToken } from 'granite-admin/utils/auth-singleton';

// Application imports
import userApi from 'granite-admin/accounts/gateways/user-api';
import commonApi from 'granite-admin/common/gateways/common-api.js';
import organisationAPIGateway from 'granite-admin/organisations/gateways/organisation-api';
import { CHANGE_PASSWORD_EVENTS, RESET_EVENTS, LOGIN_EVENTS, PROFILE_EVENTS, REGISTER_EVENTS,
// ACCOUNT_VERIFICATION_EVENTS,
CHANGE_EMAIL_EVENTS, CHANGE_PHONE_EVENTS, USER_PREFERENCES_EVENTS } from './constants';
let config = {};
if (process.env.REACT_APP_CONFIG_FILE) import(process.env.REACT_APP_CONFIG_FILE).then(({
  default: configObj
}) => config = configObj).catch(e => console.log('Module Not Found', e));
const registerDisabled = process.env.REACT_APP_REGISTER_DISABLED || false;

/**
 * Login Page - Implements of user login action.
 *
 * @param {string} username
 * @param {string} password
 *
 * @returns {json} json object representing logged in user profile.
 * @throws {GraniteError} if credentials are invalid.
 */
async function loginClicked(eventEmitter, {
  email,
  password,
  organisation_id
}, config = {}) {
  if (_.isNil(email) || _.isNil(password)) {
    throw new GraniteError('Credentials are invalid');
  }
  try {
    const data = await userApi.login(email, password, organisation_id);
    if (data.is_2fa_enabled) {
      eventEmitter.emit(LOGIN_EVENTS.LOGIN_SUCCESS, email);
    } else {
      setAuthToken(data.access_token);
      if (registerDisabled) {
        const userExist = await checkUserExistence();
        if (!userExist) return eventEmitter.emit(LOGIN_EVENTS.USER_NOT_EXIST);
      }
      if (config.subdomainRedirectEnabled) checkSubDomain(eventEmitter);else eventEmitter.emit(LOGIN_EVENTS.LOGIN_SUCCESS);
    }
  } catch (error) {
    eventEmitter.emit(LOGIN_EVENTS.LOGIN_FAILURE, error?.errors);
    // throw error;
  }
}

async function checkSubDomain(eventEmitter) {
  const host = window.location.host;
  const currentSubdomain = host.split('.')[0];
  const profile = await userApi.myProfile();
  const userSubdomain = profile?.organisations[0]?.domain || '';
  if (userSubdomain && userSubdomain !== currentSubdomain) {
    const token = getAuthToken();
    const domain = host.substring(host.indexOf('.') + 1, host.length - (window.location.port.length + (window.location.port.length ? 1 : 0)));
    resetAuthToken();
    window.location = `${window.location.protocol}//${userSubdomain}.${domain}${window.location.port ? `:${window.location.port}` : ''}/login?access_token=${token}&tokenLogin=yes`;
  } else eventEmitter.emit(LOGIN_EVENTS.LOGIN_SUCCESS);
}
async function ssoLogin(email) {
  try {
    return await userApi.ssoLogin(email);
  } catch (e) {
    throw new GraniteError(e.errors);
  }
}
async function checkUserExistence() {
  const profile = await userApi.myProfile();
  if (!profile.isSystemAdmin && profile?.organisations?.length === 0) {
    resetAuthToken();
    resetSsoLogin();
    return false;
  }
  return true;
}
/**
 * Change Email Layer - Implimentation of user change email action.
 *
 * @param {object} data - old and new email
 * @param {string} data.currentPassword - current password of user
 * @param {string} data.newEmail - new email
 *
 * @throws {GraniteError} if required keys are invalid.
 */
async function changeEmail(eventEmitter, {
  password,
  new_email
}) {
  const {
    changeEmail
  } = userApi;
  if (_.isNil(password) || _.isNil(new_email)) {
    throw new GraniteError('Current Password and New Email are required');
  }
  try {
    await changeEmail(password, new_email);
    eventEmitter.emit(CHANGE_EMAIL_EVENTS.CHANGE_EMAIL_SUCCESS);
  } catch (error) {
    eventEmitter.emit(CHANGE_EMAIL_EVENTS.CHANGE_EMAIL_FAILURE, error?.errors);
    throw error;
  }
}

/**
 * Change Email Layer - Implimentation of verify change email code action.
 *
 * @param {object} data - otp
 * @param {string} data.otp - otp
 *
 * @throws {GraniteError} if required keys are invalid.
 */
async function verifyEmailChange(eventEmitter, {
  otp
}) {
  const {
    verifyEmailChange
  } = userApi;
  if (_.isNil(otp)) {
    throw new GraniteError('OTP is required');
  }
  try {
    await verifyEmailChange(otp);
    eventEmitter.emit(CHANGE_EMAIL_EVENTS.CHANGE_EMAIL_VERIFICATION_SUCCESS);
  } catch (error) {
    eventEmitter.emit(CHANGE_EMAIL_EVENTS.CHANGE_EMAIL_VERIFICATION_FAILURE, error?.errors);
    throw error;
  }
}
/**
 * Change Phone Layer - Implimentation of user change phone action.
 *
 * @param {string} data.currentPassword - current password of user
 * @param {string} data.newPhone - new email
 *
 * @throws {GraniteError} if required keys are invalid.
 */
async function changePhone(eventEmitter, {
  password,
  new_phone
}) {
  const {
    changePhone
  } = userApi;
  if (_.isNil(password) || _.isNil(new_phone)) {
    throw new GraniteError('Current Password and New Phone are required');
  }
  try {
    await changePhone(password, `+${new_phone.number}`, new_phone.countryCode?.toUpperCase());
    eventEmitter.emit(CHANGE_PHONE_EVENTS.CHANGE_PHONE_SUCCESS);
  } catch (error) {
    eventEmitter.emit(CHANGE_PHONE_EVENTS.CHANGE_PHONE_FAILURE, error.errors);
    throw error;
  }
}

/**
 * Change Phone OTP Layer - Implimentation of verify change phone code action.
 *
 * @param {object} data - token
 * @param {string} data.token - token
 *
 * @throws {GraniteError} if required keys are invalid.
 */
async function verifyPhoneChange(eventEmitter, {
  otp
}) {
  const {
    verifyPhoneChange
  } = userApi;
  if (_.isNil(otp)) {
    throw new GraniteError('OTP is required');
  }
  try {
    await verifyPhoneChange(otp);
    eventEmitter.emit(CHANGE_PHONE_EVENTS.CHANGE_PHONE_VERIFICATION_SUCCESS);
  } catch (error) {
    eventEmitter.emit(CHANGE_PHONE_EVENTS.CHANGE_PHONE_VERIFICATION_FAILURE, error?.errors);
    throw error;
  }
}

/**
 * Change Password Layer - Implimentation of user change password action.
 *
 * @param {object} data - old and new password
 * @param {string} data.password - current password of user
 * @param {string} data.newPassword - new password
 * @param {string} data.re_password - new password confirm
 *
 * @throws {GraniteError} if required keys are invalid.
 */
async function changePasswordClicked(eventEmitter, {
  password,
  new_password,
  confirm_new_password
}) {
  const {
    changePassword
  } = userApi;
  if (_.isNil(password) || _.isNil(new_password) || _.isNil(confirm_new_password)) {
    throw new GraniteError('Password and New Password are required');
  }
  try {
    await changePassword(password, new_password, confirm_new_password);
    eventEmitter.emit(CHANGE_PASSWORD_EVENTS.CHANGE_PASSWORD_SUCCESS);
  } catch (error) {
    eventEmitter.emit(CHANGE_PASSWORD_EVENTS.CHANGE_PASSWORD_FAILURE, error.errors);
    throw error;
  }
}

/**
 * ForgotPassword Page - Implimentation of user request OTP action.
 *
 * @param {object} data
 * @param {string} data.email - email address of user
 *
 * @throws {GraniteError} if required keys are invalid.
 */
async function requestTokenClicked(eventEmitter, data) {
  const {
    requestToken
  } = userApi;
  const payload = Object.entries(data).reduce((a, [k, v]) => v === null ? a : (a[k] = v, a), {});
  try {
    await requestToken(payload);
    eventEmitter.emit(RESET_EVENTS.RESET_SUCCESS);
  } catch (error) {
    eventEmitter.emit(RESET_EVENTS.RESET_FAILURE, error?.errors?.title);
    throw error;
  }
}

/**
 * ForgotPassword Page - Implements of submit OTP action.
 *
 * @param {object} data
 * @param {string} data.token - OTP
 *
 * @returns {string} uid
 * @throws {GraniteError} if required keys are missing.
 */
async function submitTokenClicked(eventEmitter, {
  token,
  email,
  phone
}) {
  const {
    submitToken
  } = userApi;
  if (_.isNil(token)) {
    throw new GraniteError('Token is required');
  }
  if (phone) phone = phone.phone;
  try {
    const {
      message
    } = await submitToken(token, email, phone);
    eventEmitter.emit(RESET_EVENTS.RESET_TOKEN_SUCCESS, {
      message
    });
  } catch (error) {
    eventEmitter.emit(RESET_EVENTS.RESET_TOKEN_FAILURE, error?.errors?.title);
    throw error;
  }
}

/**
 * ResetPassword Page - Implimentation of resetpassword action.
 *
 * @param {object} data
 * @param {string} data.uid - uid sent from backend after submitting OTP
 * @param {string} data.password - new password of user
 * @param {string} data.re_password - new password confirm
 *
 * @throws {GraniteError} if required keys are missing.
 */
async function resetPasswordClicked(eventEmitter, {
  jwt_token,
  password,
  confirm_password
}) {
  const {
    resetPassword
  } = userApi;
  if (_.isNil(jwt_token) || _.isNil(password) || _.isNil(confirm_password)) {
    throw new GraniteError('Uid, password and re_password is required');
  }
  try {
    await resetPassword(jwt_token, password, confirm_password);
    eventEmitter.emit(RESET_EVENTS.RESET_PASSWORD_SUCCESS);
  } catch (error) {
    eventEmitter.emit(RESET_EVENTS.RESET_PASSWORD_FAILURE, error?.errors?.title);
    throw error;
  }
}

/**
 * Activate User Page - Implimentation of setpassword action.
 *
 * @param {object} data
 * @param {string} data.uid - uid sent from backend after submitting OTP
 * @param {string} data.password - new password of user
 * @param {string} data.re_password - new password confirm
 *
 * @throws {GraniteError} if required keys are missing.
 */
async function setPasswordClicked(eventEmitter, {
  jwt_token,
  password,
  confirm_password
}) {
  const {
    activateUser
  } = userApi;
  if (_.isNil(jwt_token) || _.isNil(password) || _.isNil(confirm_password)) {
    throw new GraniteError('Uid, password and re_password is required');
  }
  try {
    await activateUser(jwt_token, password, confirm_password);
    eventEmitter.emit(RESET_EVENTS.RESET_PASSWORD_SUCCESS);
  } catch (error) {
    eventEmitter.emit(RESET_EVENTS.RESET_PASSWORD_FAILURE, error?.errors?.title);
    throw error;
  }
}

/**
 * Register Page - Implimentation of register action.
 *
 * @param {object} values
 *
 * @throws {GraniteError} if required keys are missing.
 */
async function registerClicked(eventEmitter, values, type) {
  const {
    register
  } = userApi;
  if (_.isNil(values.password)) {
    throw new GraniteError('Credentials are invalid');
  }
  if (type === 'phone') {
    values = {
      ...values,
      phone: values.phone?.phone
    };
  }
  try {
    await register(values);
    // todo
    // setAuthToken(token)
    eventEmitter.emit(REGISTER_EVENTS.REGISTER_SUCCESS);
  } catch (error) {
    eventEmitter.emit(REGISTER_EVENTS.REGISTER_FAILURE, error.errors);
    throw error;
  }
}

/**
 * Profile Page - Implementation of updateProfile action.
 *
 * @param {object} user profile data
 * @param {string} data.email - user email
 * @param {string} data.first_name - user first_name
 * @param {string} data.last_name - user last_name
 * @param {string} data.username - username
 *
 * @returns {object} updatedProfile
 * @throws {GraniteError} if required keys are missing.
 */
async function updateProfileClicked(eventEmitter, {
  first_name,
  last_name
}, user = {}) {
  const {
    updateMyProfile
  } = userApi;
  if (_.isNil(first_name) && _.isNil(last_name)) {
    throw new GraniteError('Credentials are invalid');
  }
  try {
    const response = await updateMyProfile(first_name, last_name);
    let {
      permissions,
      organisations,
      permissionsName,
      currentOrganisation,
      isSystemAdmin
    } = user ?? {};
    eventEmitter.emit(PROFILE_EVENTS.PROFILE_POST_SUCCESS, {
      ...response,
      permissions,
      organisations,
      permissionsName,
      currentOrganisation,
      isSystemAdmin
    });
  } catch (error) {
    eventEmitter.emit(PROFILE_EVENTS.PROFILE_POST_FAILURE);
    throw error;
  }
}

/**
 * Profile Settings Tab - Implementation of user Settings Tab action.
 *
 * @param {Object} eventEmitter
 *
 * @returns {Object} - an array containing userPreferences list
 * @throws {GraniteError} if api failed.
 */
async function getUserPreferences(eventEmitter) {
  try {
    const userPreferences = await userApi.getUserPreferences();
    return userPreferences;
  } catch (error) {
    if (eventEmitter) eventEmitter.emit(USER_PREFERENCES_EVENTS.GET_USER_PREFERENCES_FAILURE);
    throw error;
  }
}

/**
 * Profile Settings Tab - Implementation of user Settings Tab action.
 *
 * @param {Object} eventEmitter
 * @param {int} an ID of user preference pk
 * @param {str} an value of user preference
 *
 * @throws {GraniteError} if api failed.
 */
async function changeUserPreference(eventEmitter, values) {
  try {
    await Promise.all(values.map(async val => userApi.changeUserPreference(val.pk, val.value)));
    eventEmitter.emit(USER_PREFERENCES_EVENTS.CHANGE_USER_PREFERENCES_SUCCESS);
    getMyProfile(eventEmitter);
  } catch (error) {
    eventEmitter.emit(USER_PREFERENCES_EVENTS.CHANGE_USER_PREFERENCES_FAILURE);
    throw error;
  }
}

/**
 * Profile Settings Tab - Implementation of getting master config.
 * @throws {GraniteError} if api failed.
 */
async function getMasterPreferences() {
  try {
    const list = await commonApi.getMasterConfigs('USER_PREFERENCE');
    return list;
  } catch (error) {
    throw new GraniteError(error.errors);
  }
}

/**
 * Profile Image Upload - Implementation of user Profile Image Upload.
 *
 * @param {Object} eventEmitter
 * @param {Object} values
 * @param {Array} values.files
 * @param {string} values.fileName - a value of file name
 * @param {string} values.ext - a value of file extension
 *
 * @throws {GraniteError} if api failed.
 */
async function uploadProfileImage(eventEmitter, values) {
  try {
    const {
      files,
      file_name,
      ext
    } = values;
    const {
      fields,
      url
    } = await userApi.getS3Url(`${file_name}.${ext}`);
    const fileData = new FormData();
    Object.keys(fields).forEach(field => {
      field !== 'Content-Type' && fileData.append(field, fields[field]);
    });
    fileData.append('file', files[0]);
    await userApi.uploadS3(url, fileData);
    await userApi.updateMyProfile(null, null, fields.key);
    eventEmitter.emit(PROFILE_EVENTS.IMAGE_UPLOAD_SUCCESS);
  } catch (error) {
    eventEmitter.emit(PROFILE_EVENTS.IMAGE_UPLOAD_FAILURE);
    throw error;
  }
}

/**
 * Profile Settings Tab - Implementation of user Settings Tab action.
 *
 * @param {Object} eventEmitter
 *
 * @returns {Object} - user entity
 * @throws {GraniteError} if api failed.
 */
async function getMyProfile(eventEmitter) {
  try {
    const profile = await userApi.myProfile();
    const localOrganisation = getOrganisation();
    let orgId = null;
    if (localOrganisation && localOrganisation !== 'undefined') {
      orgId = localOrganisation;
    } else if (!profile.isSystemAdmin) {
      orgId = profile.organisations[0]?.pk;
    }
    if (orgId) {
      // FETCH PERMISSIONs & Org Detials
      const [myPermissions, orgDetail] = await Promise.all([organisationAPIGateway.getEmployeePermissions(orgId), organisationAPIGateway.getOrganisationDetail(orgId)]);
      profile.setPermissions(myPermissions.map(i => i.pk));
      profile.setPermissionsName(myPermissions.map(i => i.name));
      profile.setCurrentOrganisation(orgDetail);
    } else if (profile.isSystemAdmin) {
      const myPermissions = await organisationAPIGateway.getEmployeePermissions(orgId);
      profile.setPermissions(myPermissions.map(i => i.pk));
      profile.setCurrentOrganisation({});
      profile.setPermissionsName(myPermissions.map(i => i.name));
    } else {
      profile.setPermissions([]);
      profile.setCurrentOrganisation({});
      profile.setPermissionsName([]);
    }
    const userPref = await getUserPreferences();
    if (config?.sidebarSettings?.layout) {
      profile.setNavigationLayout(config?.sidebarSettings?.layout);
    }
    userPref.forEach(pref => {
      switch (pref?.preferenceKey) {
        case 'user_timezone':
          profile.setTimezone(pref?.value);
          break;
        case 'is_2fa_enabled':
          profile.set2FA(pref?.value);
          break;
        case 'datetime_format':
          profile.setDateTimeFormat(pref?.value);
          break;
        case 'date_format':
          profile.setDateFormat(pref?.value);
          break;
        case 'time_format':
          profile.setTimeFormat(pref?.value);
          break;
        case 'navigation_bar':
          if (!config?.sidebarSettings?.layout) profile.setNavigationLayout(pref.value.replaceAll(' ', '_').toLowerCase());
          break;
        default:
          profile.setRestPreferences(pref);
          break;
      }
    });
    if (eventEmitter) eventEmitter.emit(PROFILE_EVENTS.PROFILE_POST_SUCCESS, profile);else return profile;
  } catch (error) {
    let errorMessage = JSON.stringify(error);
    if (error.response) errorMessage = error.response.data.message;
    if (error.message) errorMessage = error.message;
    if (eventEmitter) eventEmitter.emit(PROFILE_EVENTS.PROFILE_POST_FAILURE);
    throw new GraniteError(errorMessage);
  }
}
async function userInvitation(eventEmitter, values) {
  try {
    await userApi.userInvite(values);
    eventEmitter.emit('INVITE_SUCCESS');
  } catch (error) {
    if (error.errors.title) {
      throw error;
    } else {
      eventEmitter.emit('INVITE_FAILURE');
      throw error;
    }
  }
}
async function otpSubmit(eventEmitter, values) {
  try {
    const data = await userApi.otpSubmit(values);
    setAuthToken(data.access_token);
    eventEmitter.emit('OTP_SUCCESS');
  } catch (error) {
    eventEmitter.emit('OTP_FAILURE');
    throw error;
  }
}
async function resendOtp(eventEmitter, values) {
  try {
    await userApi.resendOtp(values);
    eventEmitter.emit('OTP_SEND_SUCCESS');
  } catch (error) {
    eventEmitter.emit('OTP_SEND_FAILURE');
    throw error;
  }
}
export { changeEmail, changePasswordClicked, loginClicked, registerClicked, resetPasswordClicked, requestTokenClicked, submitTokenClicked, updateProfileClicked, verifyEmailChange, changePhone, verifyPhoneChange, getUserPreferences, changeUserPreference, uploadProfileImage, getMyProfile, getMasterPreferences, userInvitation, otpSubmit, resendOtp, setPasswordClicked, ssoLogin };